import Head from 'next/head';
import React from 'react';

import {useI18n} from '@udemy/i18n';

import {META_DATA} from 'src/lib/constants';

type RobotRule = 'noindex' | 'follow';

interface HeadSeoTypes {
    title?: string;
    description?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
    ogType?: string;
    ogLocale?: string;
    canonicalUrl?: string;
    robots?: RobotRule | RobotRule[];
}

export const HeadSEO = ({
    title,
    description,
    ogDescription,
    ogTitle,
    ogImage = META_DATA.defaultMetaImage,
    ogType,
    canonicalUrl = process.env.NEXT_PUBLIC_MONOLITH_URL,
    ogLocale = 'en_US',
    robots,
}: HeadSeoTypes) => {
    const {gettext} = useI18n();
    title = title ?? gettext('Online Courses - Learn Anything, On Your Schedule | Udemy');
    description =
        description ??
        gettext(
            'Udemy is an online learning and teaching marketplace with over 204,000 courses and 54 million students. Learn programming, marketing, data science and more.',
        );
    ogDescription =
        ogDescription ??
        gettext(
            'Udemy is an online learning and teaching marketplace with over 204,000 courses and 54 million students. Learn programming, marketing, data science and more.',
        );
    ogTitle = ogTitle ?? gettext('Online Courses - Learn Anything, On Your Schedule | Udemy');

    return (
        <Head>
            <title>{title}</title>
            <meta name="description" content={description} key="description" />
            <meta name="title" content={title} key="title" />
            <meta name="medium" content="mult" />
            <meta
                name="viewport"
                content="width=device-width, initial-scale=1.0, minimum-scale=1.0"
                key="viewport"
            />
            {robots && (
                <meta
                    name="robots"
                    content={typeof robots === 'string' ? robots : robots.join(', ')}
                />
            )}
            {/* Mobile app Meta tags */}
            <meta name="apple-itunes-app" content={META_DATA.appleItunesApp} />
            <meta name="google-play-app" content={META_DATA.googlePlayApp} />
            <meta property="fb:app_id" content={META_DATA.fbAppId} />
            <meta httpEquiv="X-UA-Compatible" content="IE=Edge" />
            <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
            {/* OpenGraph */}
            <meta property="og:title" content={ogTitle} key="og:title" />
            <meta property="og:url" content={canonicalUrl} key="og:url" />
            <meta property="og:description" content={ogDescription} key="og:description" />
            <meta property="og:image" content={ogImage} key="og:image" />
            <meta property="og:type" content={ogType} />
            <meta property="og:site_name" content={META_DATA.brandTitle} />
            <meta property="og:locale" content={ogLocale} />
            {/* Twitter Meta */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:domain" content={META_DATA.domain} />
            <meta name="twitter:title" content={ogTitle} />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:description" content={ogDescription} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:site" content="@udemy" />
            {/* Itemprop */}
            <meta itemProp="name" content={ogTitle} />
            <meta itemProp="url" content={canonicalUrl} />
            <meta itemProp="description" content={ogDescription} />
            <meta itemProp="image" content={ogImage} />
            {/* Warm up the CDN connection for images. */}
            <link rel="preconnect" href="https://s.udemycdn.com/" />
            <link rel="preconnect" href="https://s.udemycdn.com/" crossOrigin="" />
            <link rel="preconnect" href="https://img-c.udemycdn.com/" />
            <link rel="preconnect" href="https://img-b.udemycdn.com/" />
            {/*For browsers which don't support subresource or preconnect.*/}
            <link rel="dns-prefetch" href="https://s.udemycdn.com/" />
            <link rel="dns-prefetch" href="https://img-c.udemycdn.com/" />
            <link rel="dns-prefetch" href="https://img-b.udemycdn.com/" />

            {/*Favicon*/}
            <link rel="apple-touch-icon" sizes="180x180" href={META_DATA.appleTouchIcon} />
            <link rel="icon" type="image/png" sizes="32x32" href={META_DATA.brandFavIcon} />
            <link rel="icon" type="image/png" sizes="16x16" href={META_DATA.brandFavIcon16x16} />
            <link rel="manifest" href={META_DATA.manifest} />

            <meta name="application-name" content={META_DATA.brandTitle} />
            <meta name="msapplication-TileColor" content={META_DATA.applicationTitleColor} />
            <meta name="msapplication-TileImage" content={META_DATA.applicationTitleImage} />
            <meta name="msapplication-config" content={META_DATA.applicationConfig} />
            <meta name="theme-color" content="#ffffff" />

            <base href={process.env.DEPLOY_BASE_PATH} />
            {/* Canonical */}
            <link rel="canonical" href={canonicalUrl} />
        </Head>
    );
};
